/**
 * Mi agenda: lo que tiene hoy quien entra.
 *
 * POR QUE ESTA PANTALLA Y NO "CITAS"
 * ==================================
 * Al radiologo y al medico remitente se les quito "Citas" porque esa pantalla
 * exige `appointment:view:all` y ellos solo tienen `view:own`: el menu ofrecia
 * una opcion que el servidor les negaba. Y ademas no es su trabajo, un
 * radiologo no da citas.
 *
 * Lo que si necesitan es lo contrario: ver lo SUYO. Lo pidio Edelmiro
 * textualmente y es lo primero que mira un medico al empezar el dia.
 *
 * LO QUE GOBIERNA EL DISENO
 * =========================
 * Es una pantalla que alguien abre todos los dias y mira diez segundos. Asi
 * que: el resumen arriba y de un vistazo, la tabla densa, y dos clics como
 * maximo para pasar del dia a la semana. Nada de tarjetas con mucho aire.
 *
 * La fecha se calcula con el reloj del NAVEGADOR, no con `toISOString()`, que
 * devuelve UTC. Es el mismo error que tenia el planificador de avisos: en
 * Bogota, a partir de las 7 de la tarde, la fecha UTC ya es la del dia
 * siguiente y la pantalla habria mostrado el dia equivocado.
 */

import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, CalendarDays, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

import { useMiAgenda } from '@/application/appointments'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import { APP_ROUTES } from '@/config/constants'
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
  type AppointmentStatus,
} from '@/domain/appointment/types'
import type { BadgeVariant } from '@/presentation/components/ui/Badge'

// =============================================================================
// Fechas, con el reloj de quien mira
// =============================================================================

/** 'YYYY-MM-DD' de una fecha local. `toISOString()` daria UTC. */
const aIso = (d: Date): string => {
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const dia = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mes}-${dia}`
}

const sumarDias = (iso: string, dias: number): string => {
  const [a, m, d] = iso.split('-').map(Number)
  const fecha = new Date(a!, m! - 1, d! + dias)
  return aIso(fecha)
}

/** El lunes de la semana a la que pertenece esa fecha. */
const lunesDe = (iso: string): string => {
  const [a, m, d] = iso.split('-').map(Number)
  const fecha = new Date(a!, m! - 1, d!)
  const desplazamiento = (fecha.getDay() + 6) % 7
  return sumarDias(iso, -desplazamiento)
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

const enLetra = (iso: string, conDia = true): string => {
  const [a, m, d] = iso.split('-').map(Number)
  const fecha = new Date(a!, m! - 1, d!)
  const dia = conDia ? `${DIAS[fecha.getDay()]} ` : ''
  return `${dia}${d} de ${MESES[m! - 1]}`
}

const horaEnLetra = (hhmm: string): string => {
  const [h, min] = hhmm.split(':').map(Number)
  if (h === undefined || min === undefined) return hhmm
  const sufijo = h < 12 ? 'a.m.' : 'p.m.'
  const doce = h % 12 === 0 ? 12 : h % 12
  return `${doce}:${String(min).padStart(2, '0')} ${sufijo}`
}

type Vista = 'dia' | 'semana'

/**
 * El catalogo de colores esta declarado como `Record<AppointmentStatus, string>`
 * y Badge espera su propia union. Se convierte aqui, en un solo sitio, en vez de
 * meter un `as any` en el JSX: si alguien anade un estado con un color que Badge
 * no tiene, esto es lo que hay que mirar.
 */
const colorDelEstado = (estado: AppointmentStatus): BadgeVariant =>
  APPOINTMENT_STATUS_COLORS[estado] as BadgeVariant

// =============================================================================

export function MiAgendaPage() {
  const navigate = useNavigate()
  const [vista, setVista] = useState<Vista>('dia')
  const [ancla, setAncla] = useState<string>(() => aIso(new Date()))

  const { desde, hasta } = useMemo(() => {
    if (vista === 'dia') return { desde: ancla, hasta: ancla }
    const lunes = lunesDe(ancla)
    return { desde: lunes, hasta: sumarDias(lunes, 6) }
  }, [vista, ancla])

  const { data, isLoading, isError } = useMiAgenda(desde, hasta)

  const mover = (direccion: -1 | 1) =>
    setAncla((actual) => sumarDias(actual, vista === 'dia' ? direccion : direccion * 7))

  const hoy = aIso(new Date())
  const esHoy = vista === 'dia' && ancla === hoy

  const rotulo =
    vista === 'dia'
      ? `${esHoy ? 'Hoy, ' : ''}${enLetra(ancla)}`
      : `Del ${enLetra(desde, false)} al ${enLetra(hasta, false)}`

  const citas = data?.citas ?? []
  const resumen = data?.resumen

  // En la semana se agrupa por dia: una lista de treinta filas sin separacion
  // no se puede leer.
  const porDia = useMemo(() => {
    const mapa = new Map<string, typeof citas>()
    for (const cita of citas) {
      const existentes = mapa.get(cita.scheduledDate) ?? []
      mapa.set(cita.scheduledDate, [...existentes, cita])
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [citas])

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mi agenda</h1>
        <p className="text-[var(--foreground-muted)]">
          Las citas en las que usted es el profesional que atiende
        </p>
      </div>

      {/* Dos clics como maximo para cambiar de dia a semana. */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div className="flex items-center gap-2">
            <Button
              variant={vista === 'dia' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setVista('dia')}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Día
            </Button>
            <Button
              variant={vista === 'semana' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setVista('semana')}
            >
              <CalendarDays className="mr-2 h-4 w-4" />
              Semana
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => mover(-1)}
              aria-label={vista === 'dia' ? 'Día anterior' : 'Semana anterior'}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[200px] text-center text-sm font-medium">{rotulo}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => mover(1)}
              aria-label={vista === 'dia' ? 'Día siguiente' : 'Semana siguiente'}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            {!esHoy && (
              <Button variant="ghost" size="sm" onClick={() => setAncla(hoy)}>
                Hoy
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* El resumen va arriba porque es lo que se mira en diez segundos. */}
      {resumen && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 text-sm">
            <span className="text-base font-semibold">
              {resumen.enPie} {resumen.enPie === 1 ? 'cita' : 'citas'} por atender
            </span>
            {resumen.porModalidad.map((m) => (
              <span key={m.codigo} className="text-[var(--foreground-muted)]">
                {m.cantidad} de {m.nombre}
              </span>
            ))}
            {resumen.atendidas > 0 && (
              <span className="text-[var(--foreground-muted)]">
                {resumen.atendidas} ya atendidas
              </span>
            )}
            {/* Las canceladas explican los huecos, asi que se dicen. */}
            {resumen.canceladas > 0 && (
              <span className="text-[var(--foreground-muted)]">
                {resumen.canceladas} canceladas
              </span>
            )}
            {resumen.noAsistieron > 0 && (
              <span className="text-[var(--foreground-muted)]">
                {resumen.noAsistieron} no asistieron
              </span>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-[var(--foreground-muted)]">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Cargando su agenda…
            </div>
          ) : isError ? (
            <div className="py-12 text-center">
              <p className="font-medium">No se pudo leer su agenda</p>
              <p className="mt-1 text-sm text-[var(--foreground-muted)]">
                Vuelva a intentarlo. Si sigue igual, avise al administrador de su institución.
              </p>
            </div>
          ) : citas.length === 0 ? (
            /* Nada de callejones sin salida: se dice por qué está vacío. */
            <div className="py-12 text-center">
              <Calendar className="mx-auto h-10 w-10 text-[var(--foreground-muted)]" />
              <p className="mt-3 font-medium">
                {vista === 'dia'
                  ? 'No tiene citas este día'
                  : 'No tiene citas esta semana'}
              </p>
              <p className="mt-1 text-sm text-[var(--foreground-muted)]">
                Aquí aparecen las citas en las que usted es el profesional que atiende. Si
                esperaba ver alguna, quien coordina la agenda puede revisarlo.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">Hora</TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Estudio</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {porDia.map(([fecha, delDia]) => (
                    <Fragment key={fecha}>
                      {vista === 'semana' && (
                        <TableRow className="bg-[var(--background-muted)]">
                          <TableCell colSpan={5} className="py-2 text-sm font-semibold">
                            {enLetra(fecha)}
                            <span className="ml-2 font-normal text-[var(--foreground-muted)]">
                              {delDia.length} {delDia.length === 1 ? 'cita' : 'citas'}
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                      {delDia.map((cita) => (
                        <TableRow
                          key={cita.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`${APP_ROUTES.APPOINTMENTS}/${cita.id}`)}
                        >
                          <TableCell className="font-medium">
                            {horaEnLetra(cita.scheduledTime)}
                          </TableCell>
                          <TableCell>{cita.patientName}</TableCell>
                          <TableCell className="text-[var(--foreground-muted)]">
                            {cita.patientDocument}
                          </TableCell>
                          <TableCell>{cita.modalityName ?? '—'}</TableCell>
                          <TableCell>
                            <Badge variant={colorDelEstado(cita.status)}>
                              {APPOINTMENT_STATUS_LABELS[cita.status as AppointmentStatus]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
